import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CommunityInvite,
  CustomFieldResponses,
  CustomFieldType,
  CustomMultipleChoiceField,
} from '../../../models/inviteService.model';
import { createEmptyCommunityInvite } from '../../../utils/formatUtils';
import { ProfileDataType, ProfileField } from '../../../types/Profile';
import InviteForm from '../../components/forms/InviteForm';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import InvitationLink from '../../components/misc/InvitationLink';
import inviteService from '../../../services/community/inviteService';
import useNavigateAfterCommunityChange from '../../../hooks/effects/useNavigateAfterCommunityChange';
import { filterCustomLabelFields } from '../../../utils/filterUtils';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import userService from '../../../services/userService';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';

export default function InviteLinkPage() {
  const location = useLocation();
  const navigate = useNavigate();
  useNavigateAfterCommunityChange();

  useEffect(() => {
    if (!location.state) navigate('/overview');
  }, []);

  if (!location.state) return <></>;
  return location.state.view === 'SETUP' ? <InviteLinkSetup /> : <LinkView />;
}

function InviteLinkSetup() {
  const community = useSelectedCommunity();

  const [invite, setInvite] = useState<CommunityInvite>(createEmptyCommunityInvite());
  const [sharedData, setSharedData] = useState<ProfileField[]>([]);
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>([]);
  const [step, setStep] = useState<number>(community?.inviteFields ? 1 : 0);

  const handleStep = (i: 1 | -1) => setStep(step + i);

  const steps = [
    <InviteView invite={invite} setInvite={setInvite} setStep={handleStep} />,
    <SharedDataView
      sharedData={sharedData}
      setStep={handleStep}
      setSharedData={setSharedData}
      customFieldResponses={customFieldResponses}
      setCustomFieldResponses={setCustomFieldResponses}
      invite={community?.inviteFields}
    />,
    <LinkView setStep={handleStep} />,
  ];

  useEffect(() => {
    if (!community?.inviteFields) return;
    inviteService.fetchInvite().then(setInvite);
  }, []);

  useEffect(() => {
    // remove all removed custom fields
    setCustomFieldResponses(
      (community?.inviteFields?.customFields ?? []).reduce((acc, field) => {
        acc[field.customFieldId!] = customFieldResponses[field.customFieldId!] ?? '';
        return acc;
      }, {} as CustomFieldResponses),
    );
  }, [JSON.stringify(community?.inviteFields?.customFields)]);

  return <StepFlow steps={steps} indexState={[step, setStep]} />;
}

interface SharedDataViewProps {
  sharedData: ProfileField[];
  setSharedData: (sharedData: ProfileField[]) => void;
  customFieldResponses: CustomFieldResponses;
  setCustomFieldResponses: (customFieldResponses: CustomFieldResponses) => void;
  invite: CommunityInvite | undefined;
  setStep: (i: 1 | -1) => void;
}

function SharedDataView({
  sharedData,
  setSharedData,
  customFieldResponses,
  setCustomFieldResponses,
  invite,
  setStep,
}: SharedDataViewProps): JSX.Element {
  const { t } = useTranslation();
  const { customFields, nonRequestedFields, mandatoryFields } = invite ?? {};
  const community = useSelectedCommunity();

  const handleSubmit = async () => {
    if (!invite || !community?.inviteFields) return;
    const mandatoryCustom = community.inviteFields.customFields.filter((f) => f.mandatory);
    if (
      mandatoryCustom.some((f) => !customFieldResponses[f.customFieldId!]) ||
      mandatoryFields!.some((f) => !sharedData.find((d) => d.dataType === f))
    ) {
      toast.error(t('toast.error.mandatoryStar'));
      return;
    }
    userService
      .updateCommunityConnection(community.id!, {
        propertyIds: sharedData.map((f) => f.id!),
        customFieldResponses,
        dataForMembers: sharedData.map((f) => f.id!),
      })
      .then(() => setStep(1));
  };

  return (
    <StepFlowStep
      setStep={setStep}
      onSubmit={handleSubmit}
      submitText={t('general.continue')}
      showNext={false}
      title={t('page.inviteLink.sharedData.title')}
      subtitle={t('page.inviteLink.sharedData.subtitle', { name: community?.name })}>
      <UserDataForm
        selectedData={sharedData}
        setSelectedData={setSharedData}
        hasSelectAll={false}
        nonRequestedFields={[
          ...(nonRequestedFields ?? []),
          ProfileDataType.SOCIAL,
        ]}
        mandatoryFields={mandatoryFields}
        onePerDataType
      />
      <CustomFieldsForm
        includeMandatory
        fields={customFields ?? []}
        response={customFieldResponses}
        setResponse={setCustomFieldResponses}
      />
    </StepFlowStep>
  );
}

interface InviteViewProps {
  invite: CommunityInvite;
  setInvite: (invite: CommunityInvite) => void;
  setStep: (i: 1 | -1) => void;
}

function InviteView({ invite, setInvite, setStep }: InviteViewProps): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();

  const handleSubmit = async (): Promise<void> => {
    const { customFields } = invite;
    const labelFields = filterCustomLabelFields(customFields);
    if (
      labelFields.some((field): boolean => !field.label) ||
      customFields
        .filter((x) => x.type === CustomFieldType.MULTIPLE_CHOICE)
        .map((x) => (x as CustomMultipleChoiceField).options)
        .flat()
        .some((option): boolean => !option)
    ) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    if (
      Array.from(new Set(labelFields.map((field) => field.label))).length !== labelFields.length
    ) {
      toast.error(t('toast.error.noTwoSame'));
      return;
    }
    await Promise.all([
      community?.inviteFields
        ? inviteService.updateInvite(invite)
        : inviteService.createInvite(invite),
    ]);
    setStep(1);
  };

  return (
    <StepFlowStep
      title={t('page.inviteLink.invite.title')}
      subtitle={t('page.inviteLink.invite.subtitle')}
      setStep={setStep}
      onSubmit={handleSubmit}
      submitText={t('general.continue')}
      showNext={false}
      showPrev={false}
      className="mt-6">
      <InviteForm invite={invite} setInvite={setInvite} />
    </StepFlowStep>
  );
}

interface LinkViewProps {
  setStep?: (i: 1 | -1) => void;
}

function LinkView({ setStep }: LinkViewProps) {
  const community = useSelectedCommunity();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <StepFlowStep
      setStep={setStep ?? (() => {})}
      showPrev={!!setStep}
      className="max-w-full"
      showNext={false}
      submitText={t('page.inviteLink.toDashboard')}
      onSubmit={() => navigate('/overview')}>
      <div className="mx-auto my-auto flex h-full w-full flex-1 flex-col items-center justify-center gap-2 md:w-[70%] md:flex-row md:gap-0">
        <img
          alt="Vera"
          src={`${process.env.PUBLIC_URL}/assets/figures/party_share.svg`}
          className="w-[40%]"
        />
        <div className="flex w-full flex-col gap-2">
          <h1 className="text-3xl font-semibold">{t('page.inviteLink.title')}</h1>
          <InvitationLink />
        </div>
      </div>
    </StepFlowStep>
  );
}
